import { inject, Injectable } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';

import {
  noWhitespaceValidator,
  NEGATIVE_POSITIVE_NUMBER_WITH_SEPARATORS_PATTERN,
  minMaxRangeValidator,
  positiveNumberValidator,
} from '@recruitee/common';
import {
  CustomField,
  CustomFieldType,
  SingleLineCustomField,
  SingleChoiceCustomField,
  MultiChoiceCustomField,
  BooleanCustomField,
  NumberFieldValue,
  MoneyFieldValue,
  DateCustomField,
  SingleUserCustomField,
  MultiUserCustomField,
  CustomFieldFormGroup,
} from '@recruitee/custom-fields-types';

@Injectable({
  providedIn: 'root',
})
export class CustomFieldFormHelpers {
  private readonly fb = inject(FormBuilder);

  // eslint-disable-next-line max-lines-per-function
  public getCustomFieldFormGroup(
    customField: CustomField,
    useCurrentValues?: boolean,
    currentCustomFieldsForm?: FormArray<CustomFieldFormGroup>,
  ): CustomFieldFormGroup {
    const {
      id,
      name,
      type,
      configuration,
      value: customFieldValueData,
      required,
      displayOnCareersSite,
    } = customField;
    const currentValue = currentCustomFieldsForm?.value.find(data => data.id === id) || {};

    switch (type) {
      case CustomFieldType.SingleLine:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          displayOnCareersSite,
          value: this.fb.group({
            value: useCurrentValues
              ? (<SingleLineCustomField>currentValue).value?.value
              : customFieldValueData.value,
          }),
        });
      case CustomFieldType.SingleChoice:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          value: this.fb.group({
            option: useCurrentValues
              ? this.fb.control((<SingleChoiceCustomField>currentValue).value?.option)
              : this.fb.control(customFieldValueData.option),
          }),
        });
      case CustomFieldType.MultiChoice:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          value: this.fb.group({
            options: this.fb.control(
              useCurrentValues
                ? (<MultiChoiceCustomField>currentValue).value?.options
                : customFieldValueData.options,
            ),
          }),
        });
      case CustomFieldType.Boolean:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          displayOnCareersSite,
          value: this.fb.group({
            value: useCurrentValues
              ? !!(<BooleanCustomField>currentValue).value?.value
              : !!customFieldValueData.value,
          }),
        });
      case CustomFieldType.Number:
        const currentNumberValueData = <NumberFieldValue>currentValue.value;
        const valueFormGroup = this.fb.group({
          ...(configuration.isRange
            ? {
                min: this.fb.control(
                  useCurrentValues ? currentNumberValueData?.min : customFieldValueData.min,
                  {
                    validators: [
                      noWhitespaceValidator,
                      positiveNumberValidator,
                      minMaxRangeValidator('min', 'max'),
                    ],
                  },
                ),
                max: this.fb.control(
                  useCurrentValues ? currentNumberValueData?.max : customFieldValueData.max,
                  {
                    validators: [
                      noWhitespaceValidator,
                      positiveNumberValidator,
                      minMaxRangeValidator('min', 'max'),
                    ],
                  },
                ),
              }
            : {
                value: this.fb.control(
                  useCurrentValues ? currentNumberValueData?.value : customFieldValueData.value,
                  {
                    validators: [
                      noWhitespaceValidator,
                      Validators.pattern(NEGATIVE_POSITIVE_NUMBER_WITH_SEPARATORS_PATTERN),
                    ],
                  },
                ),
              }),
        });

        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          value: valueFormGroup,
        });
      case CustomFieldType.Money:
        const currentMoneyValueData = <MoneyFieldValue>currentValue.value;
        const moneyValueFormGroup = this.fb.group({
          ...(configuration.isRange
            ? {
                min: this.fb.control(
                  useCurrentValues ? currentMoneyValueData?.min : customFieldValueData.min,
                  {
                    validators: [positiveNumberValidator, minMaxRangeValidator('min', 'max')],
                  },
                ),
                max: this.fb.control(
                  useCurrentValues ? currentMoneyValueData?.max : customFieldValueData.max,
                  {
                    validators: [positiveNumberValidator, minMaxRangeValidator('min', 'max')],
                  },
                ),
                currency: useCurrentValues
                  ? currentMoneyValueData?.currency
                  : customFieldValueData.currency,
              }
            : {
                value: this.fb.control(
                  useCurrentValues ? currentMoneyValueData?.value : customFieldValueData.value,
                  {
                    validators: [noWhitespaceValidator, positiveNumberValidator],
                  },
                ),
                currency: useCurrentValues
                  ? currentMoneyValueData?.currency
                  : customFieldValueData.currency,
              }),
        });

        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          value: moneyValueFormGroup,
        });
      case CustomFieldType.Date:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          displayOnCareersSite,
          value: this.fb.group({
            value: useCurrentValues
              ? (<DateCustomField>currentValue).value?.value
              : customFieldValueData.value,
          }),
        });
      case CustomFieldType.SingleUser:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          value: this.fb.group({
            admin: this.fb.control(
              useCurrentValues
                ? (<SingleUserCustomField>currentValue).value?.admin
                : customFieldValueData.admin,
            ),
          }),
        });
      case CustomFieldType.MultiUser:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          value: this.fb.group({
            admins: this.fb.control(
              useCurrentValues
                ? (<MultiUserCustomField>currentValue).value?.admins
                : customFieldValueData.admins || [],
            ),
          }),
        });

      default:
        throw new Error(`Unsupported custom field type: ${type}`);
    }
  }
}
